import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate } from "react-router";
import CheckScheduleDialog from "../components/checks/check-schedule-dialog.tsx";
import { DataGrid } from "../components/data-grid";
import { PageHeader } from "../components/page-header";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { STATUS_COLORS } from "../theme/theme";
import type { CheckSchedule, CheckScheduleSchema } from "../types.ts";

interface CheckScheduleActionColumnProps extends GridRenderCellParams<CheckSchedule> {
  onDelete: () => void;
  onToggleEnabled: () => void;
}

const CheckScheduleActionColumn = ({
  row,
  onDelete,
  onToggleEnabled,
}: CheckScheduleActionColumnProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { mutate: runSchedule, isPending: isRunningSchedule } = useApiMutation<
    void,
    { run_id: string }
  >("POST", `/check-schedules/${row.id}/run`, {
    onSuccess: () => {
      enqueueSnackbar(
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body1">Schedule run started!</Typography>
          <IconButton
            aria-label="View schedule"
            onClick={() => {
              nav(`/check-schedules/${row.id}`);
            }}
            size="small"
            color="inherit"
          >
            <OpenInNewIcon htmlColor="#fff" />
          </IconButton>
        </Stack>,
        {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
    },
  });

  const { mutate: toggleEnabled, isPending: isTogglingEnabled } = useApiMutation<
    { enabled: boolean },
    CheckSchedule
  >("PATCH", `/check-schedules/${row.id}`, {
    onSuccess: () => {
      enqueueSnackbar(`Schedule ${row.enabled ? "disabled" : "enabled"} successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      onToggleEnabled();
    },
  });

  const { mutate: deleteSchedule, isPending: isDeletingSchedule } = useApiMutation<void, void>(
    "DELETE",
    `/check-schedules/${row.id}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Schedule deleted successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        onDelete();
        setDeleteConfirmOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting schedule: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteConfirmOpen(false);
      },
    },
  );

  const handleDeleteConfirm = () => {
    deleteSchedule({});
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <Box alignItems="center" display="flex">
        <Tooltip title={row.enabled ? "Disable schedule" : "Enable schedule"}>
          <Switch
            size="small"
            checked={row.enabled}
            disabled={isTogglingEnabled}
            onChange={() => toggleEnabled({ enabled: !row.enabled })}
          />
        </Tooltip>

        <Tooltip title="Run schedule now">
          <IconButton
            size="small"
            color="default"
            disabled={isRunningSchedule}
            onClick={() => {
              runSchedule({});
            }}
            aria-label="Run schedule"
          >
            <PlayCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete schedule">
          <IconButton
            size="small"
            color="error"
            disabled={isDeletingSchedule}
            onClick={() => setDeleteConfirmOpen(true)}
            aria-label="Delete schedule"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Schedule Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the schedule "{row.name}"? This action will soft delete
            the schedule and it will no longer be visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            autoFocus
            disabled={isDeletingSchedule}
          >
            {isDeletingSchedule ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface CheckSchedulesListResponse {
  schedules: CheckSchedule[];
  total: number;
}

export default function CheckSchedulesPage() {
  const [newSchedule, setNewSchedule] = useState<CheckScheduleSchema | null>(null);
  const nav = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const {
    data: schedulesResponse,
    isLoading: isLoadingSchedules,
    refetch: refetchSchedules,
  } = useApiQuery<CheckSchedulesListResponse>(
    searchQuery ? `/check-schedules?q=${encodeURIComponent(searchQuery)}` : "/check-schedules",
  );

  const schedules = schedulesResponse?.schedules ?? [];

  const { mutate: createSchedule, isPending: isCreatingSchedule } = useApiMutation<
    CheckScheduleSchema,
    { id: string }
  >("POST", "/check-schedules", {
    onSuccess: () => {
      enqueueSnackbar("Schedule created!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      refetchSchedules();
      setNewSchedule(null);
    },
  });

  const handleDeleteSchedule = () => {
    refetchSchedules();
  };

  const handleToggleEnabled = () => {
    refetchSchedules();
  };

  const handleRowClick = (params: GridRowParams<CheckSchedule>) => {
    nav(`/check-schedules/${params.row.id}`);
  };

  const scheduleColumns: GridColDef<CheckSchedule>[] = [
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 240,
      flex: 1,
    },
    {
      field: "cron_expression",
      headerName: "Schedule",
      type: "string",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            backgroundColor: "grey.100",
          }}
        />
      ),
    },
    {
      field: "enabled",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Paused"}
          size="small"
          sx={{
            fontSize: "0.75rem",
            backgroundColor: params.value ? STATUS_COLORS.success.bg : "grey.100",
            color: params.value ? STATUS_COLORS.success.text : "grey.600",
          }}
        />
      ),
    },
    {
      field: "check_ids",
      headerName: "Checks",
      width: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (val: CheckSchedule["check_ids"]) => {
        return val?.length ?? 0;
      },
    },
    {
      field: "timezone",
      headerName: "Timezone",
      type: "string",
      width: 160,
    },
    {
      field: "created_at",
      type: "dateTime",
      headerName: "Created At",
      valueGetter: (val: CheckSchedule["created_at"]) => {
        return new Date(val);
      },
      minWidth: 180,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <CheckScheduleActionColumn
          {...params}
          onDelete={handleDeleteSchedule}
          onToggleEnabled={handleToggleEnabled}
        />
      ),
    },
  ];

  return (
    <Box data-testid="check-schedules-page">
      <PageHeader
        title="Check Schedules"
        subtitle="Manage scheduled check executions"
        sx={{ borderRadius: 3 }}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search schedules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "grey.500" }} />,
              }}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "common.white",
                },
              }}
            />
            <Tooltip title="New Schedule" placement="top">
              <IconButton
                size="small"
                onClick={() =>
                  setNewSchedule({
                    name: "",
                    app_config_id: "",
                    check_ids: [],
                    cron_expression: "",
                    timezone: "UTC",
                    enabled: true,
                  })
                }
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  border: "0px solid",
                  borderColor: "divider",
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "info.dark",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />

      {!!newSchedule && (
        <CheckScheduleDialog
          open={!!newSchedule}
          mode="create"
          onClose={() => setNewSchedule(null)}
          onSave={(data) => createSchedule(data)}
          data={newSchedule}
          setData={(data) => setNewSchedule(data as CheckScheduleSchema)}
          isProcessing={isCreatingSchedule}
        />
      )}

      <DataGrid
        loading={isLoadingSchedules}
        rows={schedules}
        columns={scheduleColumns}
        pageSizeOptions={[25]}
        minHeight={`calc(100vh - 200px)`}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
